"use client";

import Link from "@/infrastructure/router/Link";
import { useRouter } from "@/infrastructure/router/navigation";
import {
	type CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	DSFont,
	DSSpacing,
} from "@/components/design-system/ArtistDesignSystem";
import { GenArtBackground } from "./GenArtBackground";
import { GenArtBottomInfoCard } from "./GenArtBottomInfoCard";
import { GenArtHeroPreview } from "./GenArtHeroPreview";
import { GenArtModelSelectorRow } from "./GenArtModelSelectorRow";
import { GenArtViewer360 } from "./GenArtViewer360";
import {
	beginnerTutorialStore,
	circleDanceTutorial,
	tutorialGuideText,
	tutorialPrompt,
	tutorialText,
	useBeginnerTutorial,
} from "@/domain/tutorial/BeginnerTutorial";
import {
	TutorialCoachmark,
	TutorialDimmer,
	TutorialModal,
} from "@/components/tutorial/BeginnerTutorialUI";
import {
	type DesignerPromptSuggestion,
	defaultGenArtModel,
	GENART_MODELS,
	GENART_PROMPT_SUGGESTIONS,
	type GenArtModelDescriptor,
} from "./genArtCatalog";
import { useTranslation } from "@/i18n/ArtistI18n";

const UPGRADE_PURPLE = "#7373F2";

const sceneStyle: CSSProperties = {
	position: "relative",
	display: "flex",
	flexDirection: "column",
	minHeight: "100%",
	overflow: "hidden",
	background: "#05040A",
};

const toolbarStyle: CSSProperties = {
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	zIndex: 10,
	display: "grid",
	gridTemplateColumns: "1fr auto 1fr",
	alignItems: "center",
	padding: "max(12px, env(safe-area-inset-top, 0px)) 16px 8px",
	pointerEvents: "none",
};

const toolbarSideStyle = (align: "left" | "right"): CSSProperties => ({
	display: "flex",
	alignItems: "center",
	gap: "8px",
	justifyContent: align === "left" ? "flex-start" : "flex-end",
	pointerEvents: "auto",
});

const logoStyle = (viewportWidth: number): CSSProperties => ({
	font: DSFont.montHeavy(20),
	letterSpacing: "4px",
	color: "var(--ds-on-media-primary)",
	textAlign: "center",
	whiteSpace: "nowrap",
	width: viewportWidth <= 375 ? "90px" : "100px",
	justifySelf: "center",
});

const upgradeButtonStyle: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: "4px",
	padding: "6px 12px",
	borderRadius: "9999px",
	border: 0,
	font: DSFont.montHeavy(13),
	color: UPGRADE_PURPLE,
	background: "color-mix(in srgb, #7373F2 14%, transparent)",
	cursor: "pointer",
};

const iconButtonStyle: CSSProperties = {
	width: "44px",
	height: "44px",
	display: "grid",
	placeItems: "center",
	borderRadius: "50%",
	border: 0,
	color: "var(--ds-on-media-primary)",
	background: "transparent",
	cursor: "pointer",
};

const viewer360ButtonStyle: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: "10px",
	padding: "10px 14px",
	borderRadius: "9999px",
	border: 0,
	color: "var(--ds-on-media-primary)",
	background: "color-mix(in srgb, var(--ds-surface) 20%, transparent)",
	font: DSFont.genArtBold(14),
	cursor: "pointer",
};

const topHostStyle: CSSProperties = {
	position: "absolute",
	top: "max(56px, env(safe-area-inset-top, 0px))",
	left: 0,
	right: 0,
	zIndex: 9,
	paddingTop: DSSpacing.sm,
	paddingBottom: DSSpacing.lg,
};

const bottomHostStyle: CSSProperties = {
	position: "absolute",
	left: 0,
	right: 0,
	bottom: 0,
	zIndex: 8,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

function CubeGlyph({ size = 16 }: Readonly<{ size?: number }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M12 2 3 7v10l9 5 9-5V7Z" />
			<path d="M3 7l9 5 9-5" />
			<path d="M12 12v10" />
			<path d="M12 12 21 7" opacity="0.45" />
			<path d="M12 12 3 7" opacity="0.45" />
		</svg>
	);
}

function BubblesGlyph({ size = 22 }: Readonly<{ size?: number }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M7 4h6a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4H9l-4 3V8a4 4 0 0 1 2-3.46" />
			<path d="M11 12v2a4 4 0 0 0 4 4h4l3 2v-6a4 4 0 0 0-3-3.87" />
		</svg>
	);
}

export function GenArtView() {
	const { t } = useTranslation();
	const router = useRouter();
	const auth = useArtistSession();
	const tutorial = useBeginnerTutorial();

	const [selectedModel, setSelectedModel] =
		useState<GenArtModelDescriptor>(defaultGenArtModel);
	const [prompt, setPrompt] = useState("");
	const [isPromptFocused, setIsPromptFocused] = useState(false);
	const [showViewer, setShowViewer] = useState(false);
	const [animatingToViewer, setAnimatingToViewer] = useState(false);
	const [introIn, setIntroIn] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [viewportWidth, setViewportWidth] = useState(390);
	const [shouldShowUpgrade, setShouldShowUpgrade] = useState(true);

	useEffect(() => {
		const updateWidth = () =>
			setViewportWidth(typeof window === "undefined" ? 390 : window.innerWidth);
		updateWidth();
		window.addEventListener("resize", updateWidth);
		return () => window.removeEventListener("resize", updateWidth);
	}, []);

	useEffect(() => {
		setShouldShowUpgrade(auth.phase !== "user");
	}, [auth.phase]);

	useEffect(() => {
		setIntroIn(false);
		const timer = window.setTimeout(() => setIntroIn(true), 60);
		return () => window.clearTimeout(timer);
	}, []);

	const has360Entry = selectedModel.has3DModel;

	const handleSelectModel = useCallback(
		(next: GenArtModelDescriptor) => {
			if (next.id === selectedModel.id) return;
			setSelectedModel(next);
			setShowViewer(false);
			setAnimatingToViewer(false);
		},
		[selectedModel.id],
	);

	const handleStartViewer = useCallback(() => {
		if (!has360Entry) return;
		setAnimatingToViewer(true);
		window.setTimeout(() => {
			setShowViewer(true);
		}, 400);
	}, [has360Entry]);

	const handleCloseViewer = useCallback(() => {
		setAnimatingToViewer(false);
		setShowViewer(false);
	}, []);

	const handleGenerate = useCallback(() => {
		if (tutorial.step === "generateMusic") {
			if (!beginnerTutorialStore.debit(selectedModel.musicGenerationPrice)) return;
			setIsGenerating(true);
			window.setTimeout(() => {
				beginnerTutorialStore.createLocalTrack();
				beginnerTutorialStore.advance("generateMusic", "openLibrary");
				setIsGenerating(false);
			}, 750);
			return;
		}
		if (auth.phase !== "user") {
			router.push("/auth");
			return;
		}
		if (prompt.trim().length === 0) return;
		setIsGenerating(true);
		window.setTimeout(() => setIsGenerating(false), 1800);
	}, [auth.phase, prompt, router, selectedModel.musicGenerationPrice, tutorial.step]);

	const suggestions: readonly DesignerPromptSuggestion[] = useMemo(() => {
		if (!tutorial.step.startsWith("selectMusic") && tutorial.step !== "generateMusic") {
			return tutorial.step === "inactive" ? GENART_PROMPT_SUGGESTIONS : [];
		}
		return [{
			id: "circle-dance-v1",
			caption: "Circle Dance",
			prompt: tutorialPrompt("music"),
			artworkGradient: "linear-gradient(135deg, #7373F2, #E0589E)",
			artworkURL: circleDanceTutorial.assets.cover.public_url,
		}];
	}, [tutorial.step]);

	const heroOpacity = (introIn ? 1 : 0) * (animatingToViewer ? 0 : 1);
	const heroOffsetY = introIn ? 0 : 24;

	return (
		<section
			className="genart-view"
			data-viewer-open={showViewer ? "true" : "false"}
			style={sceneStyle}
		>
			<div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
				<GenArtBackground />
			</div>

			<div
				style={{
					position: "absolute",
					inset: 0,
					zIndex: 1,
					opacity: showViewer ? 0 : heroOpacity,
					transform: `translateY(${heroOffsetY}px)`,
					transition:
						"opacity 600ms cubic-bezier(0.34, 1.56, 0.64, 1), transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1)",
				}}
			>
				<GenArtHeroPreview model={selectedModel} />
			</div>

			{showViewer ? (
				<div
					style={{
						position: "absolute",
						inset: 0,
						zIndex: 2,
						animation:
							"genart-viewer-in 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
					}}
				>
					<GenArtViewer360
						tint={selectedModel.callToActionTint}
						onClose={handleCloseViewer}
					/>
				</div>
			) : null}

			<header style={toolbarStyle}>
				<div style={toolbarSideStyle("left")}>
					{tutorial.step !== "inactive" ? (
						<button
							type="button"
							style={{ ...upgradeButtonStyle, color: "var(--ds-on-media-primary)" }}
							onClick={beginnerTutorialStore.exit}
						>
							<ArtistIcon name="close" size={16} />
							<span>{tutorialText("退出教学模式", "退出教學模式", "Exit tutorial mode")}</span>
						</button>
					) : showViewer ? (
						<button
							type="button"
							aria-label={t("genart.back")}
							style={iconButtonStyle}
							onClick={handleCloseViewer}
						>
							<ArtistIcon name="back" size={22} />
						</button>
					) : shouldShowUpgrade ? (
						<Link
							href="/billing/membership"
							aria-label={t("genart.upgrade")}
							style={upgradeButtonStyle}
						>
							<ArtistIcon name="sparkle" size={14} />
							<span>{t("genart.upgrade")}</span>
						</Link>
					) : null}
				</div>
				{tutorial.step === "inactive" ? <Link
					href="/discover"
					aria-label={t("genart.home")}
					style={{
						pointerEvents: "auto",
						...logoStyle(viewportWidth),
					}}
				>
					ARTIST
				</Link> : <div style={logoStyle(viewportWidth)}>ARTIST</div>}
				<div style={toolbarSideStyle("right")}>
					{tutorial.step === "inactive" ? (
						<>
							<Link href="/messages/inbox" aria-label={t("genart.messages")} style={iconButtonStyle}>
								<BubblesGlyph size={22} />
							</Link>
							<button
								type="button"
								style={iconButtonStyle}
								aria-label={tutorialText("新手教学", "新手教學", "Tutorial")}
								onClick={beginnerTutorialStore.requestStart}
							>
								<ArtistIcon name="sparkle" size={22} />
							</button>
						</>
					) : null}
				</div>
			</header>

			{!showViewer ? (
				<div style={{ ...topHostStyle, opacity: tutorial.step === "inactive" ? 1 : 0.35, pointerEvents: tutorial.step === "inactive" ? "auto" : "none" }}>
					<GenArtModelSelectorRow
						models={GENART_MODELS}
						selectedModel={selectedModel}
						onSelect={handleSelectModel}
					/>
				</div>
			) : null}

			{!showViewer ? (
				<div style={bottomHostStyle}>
					{has360Entry && !isPromptFocused && tutorial.step === "inactive" ? (
						<div style={{ paddingBottom: DSSpacing.md }}>
							<button
								type="button"
								aria-label={t("genart.viewer360")}
								style={viewer360ButtonStyle}
								onClick={handleStartViewer}
							>
								<CubeGlyph size={16} />
								<span>360</span>
							</button>
						</div>
					) : null}
					<GenArtBottomInfoCard
						selectedModel={selectedModel}
						prompt={prompt}
						onPromptChange={setPrompt}
						isPromptFocused={isPromptFocused}
						onPromptFocusChange={setIsPromptFocused}
						promptSuggestions={suggestions}
						isPromptSuggestionsLoading={false}
						isGenerating={isGenerating}
						tutorialMode={tutorial.step !== "inactive"}
						tutorialSimulatedCredits={tutorial.simulatedCredits}
						onPromptSuggestionSelect={(suggestion) => {
							if (suggestion.id === "circle-dance-v1") {
								beginnerTutorialStore.advance("selectMusicPrompt", "generateMusic");
							}
						}}
						onGenerate={handleGenerate}
					/>
				</div>
			) : null}
			{tutorial.step !== "inactive" && tutorial.step !== "intro" ? <TutorialDimmer /> : null}
			{tutorial.confirmationVisible ? (
				<TutorialModal
					title={tutorialText("是否进入新手教学？", "是否進入新手教學？", "Enter the beginner tutorial?")}
					text={tutorialText("教学期间只使用模拟灵感，不会扣除您的真实余额。", "教學期間只使用模擬靈感，不會扣除您的真實餘額。", "The tutorial uses simulated inspo and never charges your real balance.")}
					actionLabel={tutorialText("进入教学", "進入教學", "Enter tutorial")}
					onAction={() => {
						setPrompt("");
						beginnerTutorialStore.begin();
					}}
					onCancel={beginnerTutorialStore.cancelStart}
				/>
			) : null}
			{tutorial.step === "intro" ? (
				<TutorialModal
					title={tutorialText("一起创作 Circle Dance", "一起創作 Circle Dance", "Create Circle Dance together")}
					text={tutorialGuideText("intro")}
					actionLabel={tutorialText("开始", "開始", "Start")}
					onAction={() => beginnerTutorialStore.advance("intro", "selectMusicPrompt")}
				/>
			) : null}
			{tutorial.step !== "inactive" && tutorial.step !== "intro" ? (
				<TutorialCoachmark
					step={tutorial.step}
					text={tutorialGuideText(tutorial.step)}
					position={tutorial.step === "generateMusic" ? "top" : "bottom"}
				/>
			) : null}
		</section>
	);
}
